import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, catchError, of, switchMap } from 'rxjs';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';

/**
 * Attaches a Google reCAPTCHA v3 token (header `X-Recaptcha-Token`) to the public,
 * un-authenticated POST submissions a bot could script against - the permit application
 * submits, the business application, and the "email me my permits" link request.
 *
 * v3 is invisible/score-based: no challenge, just a token minted per request that the backend
 * (RecaptchaSubscriber) scores. The matching set here MUST mirror the backend's PROTECTED_PATHS.
 * Token-gated flows (host-confirm, {token}/verify, payment-intent, resume, edit/*) are left
 * alone - their 48-char token is their gate.
 */
@Injectable()
export class RecaptchaInterceptor implements HttpInterceptor {
    /**
     * Matches the exact public POST endpoints to protect, against the full request URL
     * (which carries the apiUrl origin + path). Kept in lockstep with the backend list.
     */
    private static readonly PROTECTED = new RegExp(
        '/api/public/(' +
            'permit/(tourist-pass|recreation|friends-family|work|ltr|media|fishing|hunting|research|mine/request-link)' +
            '|business-application' +
            ')(\\?.*)?$',
    );

    constructor(private readonly recaptcha: ReCaptchaV3Service) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (req.method !== 'POST' || !RecaptchaInterceptor.PROTECTED.test(req.url)) {
            return next.handle(req);
        }

        return this.recaptcha.execute('submit').pipe(
            // catchError sits BEFORE switchMap so it only covers token minting (script blocked,
            // network blip) - not the downstream HTTP call. On failure we fall through with an
            // empty token: the backend returns its own "could not verify" message, which the
            // form already surfaces, rather than the browser hard-failing or double-firing.
            catchError(() => of('')),
            switchMap(token => next.handle(token ? req.clone({ setHeaders: { 'X-Recaptcha-Token': token } }) : req)),
        );
    }
}
